import React, { useEffect, useRef, useMemo } from 'react';
import cn from 'classnames';
import NobleIcon from '../../../components/nobleIcon';
import './audienceDropdown.scss';

import { useSelectOptionsQuery } from '../../../hooks/queries/use-select.queries';
import { toast } from 'react-toastify';
import { toastOptions } from 'helpers/colorConstants';

function AudienceDropdown({
	currentAudience, // array of numbers (venues ids)
	setCurrentAudience,
	isOpen,
	onToggle,
	displayLabel,
	emptyLabel = 'All', // text shown with no venues selected
}) {
	const dropdownRef = useRef(null);

	const { data, isLoading, error } = useSelectOptionsQuery();

	const audiences = useMemo(() => {
		if (!data || data.error) return [];
		return data.venues || [];
	}, [data]);

	useEffect(() => {
		const handleClickOutside = (event) => {
			if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
				onToggle();
			}
		};

		if (isOpen) {
			document.addEventListener('mousedown', handleClickOutside);
		}

		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [isOpen, onToggle]);

	const handleCheckboxChange = (optionId, isChecked) => {
		let newSelected = [...currentAudience];

		if (isChecked) {
			if (!newSelected.includes(optionId)) {
				newSelected.push(optionId);
			}
		} else {
			newSelected = newSelected.filter((id) => id !== optionId);
		}

		setCurrentAudience(newSelected);
	};

	const isChecked = (optionId) => {
		return currentAudience.includes(optionId);
	};

	const getDisplayText = () => {
		if (currentAudience.length === 0) {
			return emptyLabel;
		}
		if (currentAudience.length === 1) {
			const selected = audiences.find((a) => a.value === currentAudience[0]);
			return selected ? selected.label : emptyLabel;
		}
		return `${currentAudience.length} selected`;
	};

	useEffect(() => {
		if (error) {
			toast.error('Error fetching audiences data', toastOptions);
		}
	}, [error]);

	return (
		<div className="AudienceDropdown" ref={dropdownRef}>
			{displayLabel && (
				<span className="AudienceDropdown__label">{displayLabel}</span>
			)}
			<div className="AudienceDropdown__trigger" onClick={onToggle}>
				<span className="AudienceDropdown__trigger--text">
					{getDisplayText()}
				</span>
				<NobleIcon
					name="arrowDown"
					iconWrapperClass={cn('AudienceDropdown__trigger--icon', {
						'is-open': isOpen,
					})}
				/>
			</div>
			{isOpen && (
				<div className="AudienceDropdown__menu">
					{audiences.map((option) => (
						<div key={option.value} className="AudienceDropdown__option">
							<label className="AudienceDropdown__option--label">
								<input
									type="checkbox"
									checked={isChecked(option.value)}
									onChange={(e) =>
										handleCheckboxChange(option.value, e.target.checked)
									}
									className="AudienceDropdown__checkbox"
								/>
								<span className="AudienceDropdown__option--text">
									{option.label}
								</span>
							</label>
						</div>
					))}
				</div>
			)}
		</div>
	);
}

export default AudienceDropdown;
